import React, { ReactNode } from 'react';
import { IonHeader, IonToolbar, IonTitle, IonButtons, IonCard, IonCardContent, IonAvatar, IonIcon, IonRow, IonCol } from '@ionic/react';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';

interface Props {
    title: string;
    titleNode?: ReactNode;
    startButtons?: ReactNode;
    endButtons?: ReactNode;
    children?: ReactNode;
    // All styling passed from page
    headerClassName?: string;
    toolbarClassName?: string;
    smallTitleClassName?: string;
    titleHeaderClassName?: string;
    headerAvatarClassName?: string;
    // Avatar support
    avatarUrl?: string;
    avatarInitials?: string;
}

// Title card component to be used as Virtuoso Header
interface TitleCardProps {
    title: string;
    // Optional secondary line under the title (e.g. company name when Display Name
    // preference makes the title company-primary) — purely additive, other callers
    // that don't pass it are unaffected.
    subtitle?: string;
    subtitleClassName?: string;
    // All styling passed from page
    cardClassName?: string;
    titleContainerClassName?: string;
    titleClassName?: string;
    titleAvatarClassName?: string;
    avatarUrl?: string;
    avatarInitials?: string;
    // Icon badge alternative to a photo avatar — for pages with no contact/photo
    // to show (e.g. a settings list), a small icon glyph in a circle instead.
    // Ignored when avatarUrl/avatarInitials are also passed.
    titleIcon?: string;
    titleIconClassName?: string;
    cardGradient?: string;
    cardColor?: string;
    children?: ReactNode;
}

export const TitleCard = React.forwardRef<HTMLIonCardElement, TitleCardProps>(({
    title,
    subtitle,
    subtitleClassName,
    cardClassName,
    titleContainerClassName,
    titleClassName,
    titleAvatarClassName,
    avatarUrl,
    avatarInitials,
    titleIcon,
    titleIconClassName,
    cardGradient,
    cardColor,
    children,
}, ref) => (
    <IonCard
        ref={ref}
        className={cardClassName}
        style={{
            ...(cardGradient && { background: cardGradient }),
            ...(cardColor && { color: cardColor })
        }}
    >
        <IonCardContent>
            <IonRow className={titleContainerClassName}>
                {(avatarUrl || avatarInitials) ? (
                    <IonCol size="auto" style={{ padding: 0 }}>
                        <IonAvatar className={titleAvatarClassName}>
                            <img
                                src={proxyImage(avatarUrl ?? '') || getDefaultAvatar(title)}
                                alt={title}
                                onError={(e) => {
                                    const imgElement = e.target as HTMLImageElement;
                                    imgElement.src = getDefaultAvatar(title);
                                }}
                            />
                        </IonAvatar>
                    </IonCol>
                ) : titleIcon && (
                    <IonCol size="auto" style={{ padding: 0 }}>
                        <IonAvatar className={titleIconClassName}>
                            <IonIcon icon={titleIcon} />
                        </IonAvatar>
                    </IonCol>
                )}
                <IonCol style={{ padding: 0, textAlign: 'center' }}>
                    {/* size="small" avoids Ionic's ios-mode default IonTitle CSS, which is
                        position: absolute — meant for centering inside an IonToolbar, but
                        this title lives in an IonCardContent and would otherwise collapse
                        out of flow, overlapping the subtitle below it. */}
                    <IonTitle size="small" className={titleClassName} style={{ textAlign: 'center', padding: 0 }}>{title}</IonTitle>
                    {subtitle && <p className={subtitleClassName}>{subtitle}</p>}
                </IonCol>
            </IonRow>
            {children}
        </IonCardContent>
    </IonCard>
));

TitleCard.displayName = 'TitleCard';

const BasePageWithHeader = React.forwardRef<HTMLIonHeaderElement, Props>(({
    title,
    titleNode,
    startButtons,
    endButtons,
    children,
    headerClassName,
    toolbarClassName,
    smallTitleClassName,
    titleHeaderClassName,
    headerAvatarClassName,
    avatarUrl,
    avatarInitials,
}, ref) => (
    <>
        <IonHeader ref={ref} className={`ion-no-border ${headerClassName ?? ''}`}>
            <IonToolbar className={toolbarClassName}>
                {startButtons && <IonButtons slot="start">{startButtons}</IonButtons>}
                <IonTitle size="small" className={smallTitleClassName} data-header-title>
                    <IonRow className={titleHeaderClassName}>
                        {(avatarUrl || avatarInitials) && (
                            <IonAvatar className={headerAvatarClassName}>
                                <img
                                    src={proxyImage(avatarUrl ?? '') || getDefaultAvatar(title)}
                                    alt={title}
                                    onError={(e) => {
                                        const imgElement = e.target as HTMLImageElement;
                                        imgElement.src = getDefaultAvatar(title);
                                    }}
                                />
                            </IonAvatar>
                        )}
                        {titleNode ?? <span>{title}</span>}
                    </IonRow>
                </IonTitle>
                {endButtons && <IonButtons slot="end">{endButtons}</IonButtons>}
            </IonToolbar>
        </IonHeader>
        {children}
    </>
));

BasePageWithHeader.displayName = 'BasePageWithHeader';

export default BasePageWithHeader;
